"use client";

import React from "react";
import AppointmentSummary from "../appointmentSummary";
import { BOOK_AN_APPOINTMENT_UNIVERS } from "../../../../../constants/common";

interface StoreAppointmentSummaryProps {
  selectedOption: "commercial" | "phone" | "CAD";
  data: {
    store?: any;
    prenom?: string;
    nom?: string;
    telephone?: string;
    date?: string;
    slot?: string | null;
    storeName?: string;
    storeAddress?: string;
    visitAddress?: string;
    projectLabel?: string;
    Univers?: string[];
    comment?: string;
    rue?: string;
    ville?: string;
    codePostal?: string;
    type?: string;
  };
}

const appointmentData = {
  commercial: {
    reminderTitle: "📞 Un conseiller va vous rappeler",
    reminderDescription:
      "Un conseiller Lapeyre vous contactera rapidement pour confirmer votre rendez-vous et échanger avec vous sur votre projet. Cet échange nous permettra de mieux vous accompagner lors de notre rencontre en magasin.",
    stepsTitle: "Vos prochaines étapes",
    steps: [
      "<strong>Un conseiller vous rappelle rapidement</strong> pour qualifier votre projet et confirmer le rendez-vous",
      "Vous recevrez <strong>un email de confirmation</strong> avec tous les détails après l'appel",
      "Le jour J, présentez-vous à l'accueil du magasin avec vos mesures et photos si possible",
    ],
    sections: [
      {
        key: "date_time",
        label: "DATE ET HEURE",
        title: "",
        description: "",
        // link: { text: "Ajouter à mon agenda", href: "#" },
      },
      {
        key: "location",
        label: "LIEU",
        title: "",
        description: ``,
      },
      {
        key: "appointment_object",
        label: "OBJET DU RENDEZ-VOUS",
        title: "",
      },
    ],
    sectionsOrder: ["reminder", "details", "steps"],
  },

  phone: {
    reminderTitle: "Quand serez-vous rappelé(e) ?",
    reminderDescription:
      "Un conseiller Lapeyre vous contactera prochainement sur le numéro indiqué. Il prendra le temps d'échanger avec vous pour bien comprendre votre projet et vous orienter vers les meilleures solutions.",
    stepsTitle: "Comment se déroulera l'appel ?",
    steps: [
      "Le conseiller <strong>qualifie votre demande</strong> et comprend vos besoins (budget, délais, contraintes...)",
      "Il vous <strong>oriente</strong> vers les solutions les plus adaptées à votre projet",
      "Vous pourrez planifier <strong>un rendez-vous en magasin</strong> ou <strong>à domicile</strong> si nécessaire",
    ],
    messageTitle: "Numéro masqué ?",
    messageDescription:
      "Notre conseiller peut vous appeler depuis un numéro commençant par 01 ou 09. Pensez à décrocher même si le numéro ne vous est pas familier.",
    sections: [
      {
        key: "phone",
        label: "Numéro de téléphone",
        title: "",
      },
      {
        key: "date_time",
        label: "Créneau horaire souhaité",
        title: "",
        description: "",
      },
      {
        key: "appointment_object",
        label: "Objet de votre demande",
        title: "",
      },
    ],
    sectionsOrder: ["details", "reminder", "messages", "steps"],
  },

  CAD: {
    reminderTitle: "📞 Un conseiller va vous rappeler",
    reminderDescription:
      "<strong>Un conseiller Lapeyre</strong> vous contactera pour échanger sur votre projet et confirmer les modalités de la visite à domicile. Cet échange nous permet de <strong>préparer au mieux l'intervention</strong> et de vous envoyer l'expert le plus adapté à vos besoins.",
    stepsTitle: "Vos prochaines étapes",
    steps: [
      "<strong>Un conseiller vous rappelle rapidement</strong> pour qualifier votre projet et confirmer la visite",
      "Vous recevrez <strong>un email de confirmation</strong> du rendez-vous à votre domicile après l'appel",
      "Le jour J, assurez-vous que <strong>l'accès aux pièces</strong> concernées soit dégagé",
    ],
    sections: [
      {
        key: "date_time",
        label: "DATE ET HEURE",
        title: "",
        description: "",
        // link: { text: "Ajouter à mon agenda", href: "#" },
      },
      {
        key: "address",
        label: "Adresse de la visite",
        title: "",
        description: "",
      },
      {
        key: "appointment_object",
        label: "Objet de la visite",
        title: "",
        // description: "Étude technique et devis personnalisé",
      },
    ],
    sectionsOrder: ["reminder", "details", "steps"],
  },
} as const;

const VISIO_PHONE_STEPS = [
  "Le conseiller vous <strong>contacte</strong> à l’heure du rendez-vous et vous envoie un lien de visio, à ouvrir sur votre smartphone (aucune installation nécessaire)",
  "En visio, vous montrez votre projet en présentant votre espace et vos besoins (dimensions, implantation, contraintes…)",
  "Le conseiller vous <strong>oriente</strong> vers les solutions les plus adaptées à votre projet",
];

const formatDateToFrenchLabel = (dateStr?: string): string | undefined => {
  if (!dateStr) return undefined;

  const [day, month, year] = dateStr.split("/").map(Number);
  if (!day || !month || !year) return undefined;

  const date = new Date(year, month - 1, day);

  return date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
};

const extractDateFromComment = (comment?: string): string | undefined => {
  if (!comment) return undefined;

  const match = comment.match(/\b\d{2}\/\d{2}\/\d{4}\b/);
  return match?.[0];
};

const extractTimeFromComment = (comment?: string): string | undefined => {
  if (!comment) return undefined;
  return comment.match(/\b\d{2}:\d{2}\s*-\s*\d{2}:\d{2}\b/)?.[0];
};

const capitalizeAllWords = (text?: string) => {
  if (!text) return text;

  return text
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const mapUniversValuesToTitles = (univers?: string[]) => {
  if (!univers || univers.length === 0) return undefined;

  return univers
    .map((value) => {
      const found = BOOK_AN_APPOINTMENT_UNIVERS.find((u) => u.value === value);
      return found?.title || value;
    })
    .join(", ");
};

const StoreAppointmentSummary: React.FC<StoreAppointmentSummaryProps> = ({ selectedOption, data }) => {
  const baseConfig = appointmentData[selectedOption];
  const isVisioPhone = selectedOption === "phone" && data.type === "visio";

  const base = {
    ...baseConfig,
    steps: isVisioPhone ? VISIO_PHONE_STEPS : baseConfig.steps,
  };

  console.log("data", data);
  const sections = base.sections?.map((section) => {
    if (section.key === "appointment_object" && data.Univers?.length) {
      return {
        ...section,
        title: mapUniversValuesToTitles(data.Univers),
      };
    }

    if (section.key === "address" && (data.rue?.length || data.ville?.length || data.codePostal?.length)) {
      return {
        ...section,
        title: data.rue || undefined,
        description: [data.codePostal, data.ville].filter(Boolean).join(" ") || undefined,
      };
    }

    if (section.key === "phone" && data.telephone?.length) {
      return {
        ...section,
        title: data.telephone || undefined,
      };
    }

    if (section.key === "location" && data.store) {
      const { label, address, postalCode, city, country } = data.store;

      return {
        ...section,
        title: label ?? section.title,
        description: [address, `${postalCode ?? ""} ${city ?? ""} ${country ?? ""}`.trim()].filter(Boolean).join("\n"),
      };
    }

    if (section.key === "date_time") {
      const rawDate = extractDateFromComment(data.comment);
      const time = extractTimeFromComment(data.comment);
      const formattedDate = formatDateToFrenchLabel(rawDate);

      return {
        ...section,
        title: capitalizeAllWords(formattedDate ?? section.title),
        description: capitalizeAllWords(time ?? section.description),
      };
    }

    return section;
  });

  return <AppointmentSummary {...base} steps={base.steps ? [...base.steps] : undefined} sections={sections} />;
};

export default StoreAppointmentSummary;
